// Client/src/components/Profile/DeletionPendingModal.tsx
import React, { useState, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalBody,
  Text, Button, VStack, Box, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../config/firebase.ts';
import { API_URL } from '../../config/api.ts';

interface DeletionPendingModalProps {
  isOpen: boolean;
  requestedAt: string;
  scheduledFor: string;
  onCancel: () => void;
  onContinue: () => void;
}

const DeletionPendingModal: React.FC<DeletionPendingModalProps> = ({
  isOpen, requestedAt, scheduledFor, onCancel, onContinue,
}) => {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

  const { daysLeft, progress } = useMemo(() => {
    const now = Date.now();
    const end = new Date(scheduledFor).getTime();
    const start = new Date(requestedAt).getTime();
    const total = end - start;
    const elapsed = now - start;
    const left = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    const pct = Math.min(100, Math.max(0, (elapsed / total) * 100));
    return { daysLeft: left, progress: pct };
  }, [requestedAt, scheduledFor]);

  const handleCancel = async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser!.getIdToken();
      const res = await fetch(`${API_URL}/api/user/cancel-deletion`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast({ title: t('deletion.cancelledTitle'), description: t('deletion.accountRestored'), status: 'success', duration: 4000 });
      onCancel();
    } catch {
      toast({ title: t('error'), description: t('deletion.cancelError'), status: 'error', duration: 4000 });
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onContinue} isCentered size="md" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.800" />
      <ModalContent
        bg="#0f0a1a"
        border="1px solid"
        borderColor="red.900"
        boxShadow="0 0 40px rgba(220, 38, 38, 0.15)"
      >
        <ModalBody py={8} px={6}>
          <VStack spacing={5} textAlign="center">

            <Text fontSize="3xl">⚠️</Text>

            <Text color="red.300" fontWeight="800" fontSize="lg">
              {t('deletion.scheduledTitle')}
            </Text>

            <Box
              w="full"
              bg="whiteAlpha.50"
              borderRadius="10px"
              p={4}
              border="1px solid"
              borderColor="whiteAlpha.100"
            >
              <VStack spacing={3} align="stretch">
                <Text color="gray.300" fontSize="sm" lineHeight="tall">
                  {t('deletion.requestedOnText')}{' '}
                  <strong style={{ color: 'white' }}>{fmt(requestedAt)}</strong>.
                </Text>
                <Text color="gray.300" fontSize="sm" lineHeight="tall">
                  {t('deletion.gdprDeleteText')}{' '}
                  <strong style={{ color: '#f87171' }}>{t('deletion.permanentlyDeleted')}</strong> {t('deletion.onDate')}{' '}
                  <strong style={{ color: '#f87171' }}>{fmt(scheduledFor)}</strong>.
                </Text>
              </VStack>
            </Box>

            {/* Barre de progression du délai */}
            <Box w="full">
              <Box
                w="full"
                h="4px"
                bg="whiteAlpha.100"
                borderRadius="full"
                overflow="hidden"
              >
                <Box
                  h="full"
                  w={`${progress}%`}
                  bgGradient="linear(to-r, orange.400, red.500)"
                  borderRadius="full"
                  transition="width 0.5s ease"
                />
              </Box>
              <Text fontSize="xs" color="gray.500" mt={1.5}>
                {daysLeft > 0
                  ? <>{t('deletion.daysRemaining', { count: daysLeft })}</>
                  : <>{t('deletion.imminentDeletion')}</>
                }
              </Text>
            </Box>

            {/* Ce qui sera supprimé */}
            <Box w="full" fontSize="xs" color="gray.500" textAlign="left" px={1}>
              <Text mb={1}>🗑️ <strong style={{ color: '#a0aec0' }}>{t('deletion.deletedLabel')}</strong> {t('deletion.deletedItemsFull')}</Text>
              <Text>🔮 <strong style={{ color: '#a0aec0' }}>{t('deletion.anonymizedLabel')}</strong> {t('deletion.anonymizedScores')}</Text>
            </Box>

            <Box w="full" h="1px" bg="whiteAlpha.100" />

            {/* Actions */}
            <Button
              w="full"
              size="lg"
              bg="purple.600"
              color="white"
              fontWeight="700"
              _hover={{ bg: 'purple.500' }}
              isLoading={loading}
              onClick={handleCancel}
              borderRadius="10px"
            >
              {t('deletion.cancelDeletion')}
            </Button>

            <Button
              w="full"
              variant="ghost"
              color="gray.600"
              size="sm"
              onClick={onContinue}
              _hover={{ color: 'gray.400' }}
            >
              {t('deletion.continueScheduled')}
            </Button>

          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DeletionPendingModal;
